import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import cx from 'classnames';
import { InfoButton } from 'components/Buttons';
import Constants from 'config/Constants';
import _ from 'lodash';
import { useEntityTemplateParameters } from 'pages/documents_v2/hooks/useEntityTemplateParameters';
import { useGetArea } from 'pages/documents_v2/hooks/useGetArea';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { useGetTemplate } from 'pages/documents_v2/hooks/useGetTemplate';
import { BlockTypeContent } from 'pages/documents_v2/views/edit_area/blocks/Block';
import { BlockName } from 'pages/documents_v2/views/edit_area/blocks/BlockName';
import { BlockType } from 'pages/documents_v2/views/edit_area/blocks/BlockType';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { useGetAreaId } from 'pages/documents_v2/views/edit_area/hooks/useGetAreaId';
import DocumentHelper from 'pages/global/DocumentHelper';
import { useContext, useMemo } from 'react';
import { ChevronLeft } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { NumberParam, useQueryParam } from 'use-query-params';

import HelperFunctions from '../../../global/HelperFunctions';

export function AddTemplateBlock({ closeModal, variants = [], baseVariantId }) {
    const areaId = useGetAreaId();
    const area = useGetArea(areaId);
    const document = useGetDocument();
    const template = useGetTemplate(document?.modelId, document?.modelSource) ?? [];

    const areas = useMemo(() => {
        const sections = _.get(template, 'sections', []);

        if (_.isArray(sections)) {
            return sections.flatMap((section) => section.areas);
        }

        return Object.values(sections).flatMap((section) => section.areas);
    }, [template]);

    const thisAreas = area && areas ? areas.filter((a) => a.id === area.properties.parentId) : [];

    const areaBlocks = thisAreas
        .flatMap((area) => area.blocks)
        .map((block) => ({
            ...block,
            documentVariants: block.documentVariants ?? [],
            latestContent: block.baseContent,
            key: block.id,
        }));

    const blockIdsInLayout = DocumentHelper.getBlockIdsInLayout(areaBlocks);

    const blocksToRender = useMemo(() => {
        if (blockIdsInLayout.length === 0) {
            return areaBlocks;
        }

        return areaBlocks.filter((block) => !blockIdsInLayout.includes(block.id));
    }, [areaBlocks, blockIdsInLayout]);

    return (
        <>
            {blocksToRender.map((block, index) => (
                <TemplateBlockCard
                    block={block}
                    index={index}
                    areaBlocks={areaBlocks}
                    variants={variants}
                    closeModal={closeModal}
                    baseVariantId={baseVariantId}
                    key={`template-block-${block.id}`}
                />
            ))}
        </>
    );
}

function TemplateBlockCard({ block, index, areaBlocks, variants = [], closeModal, baseVariantId }) {
    const { t } = useTranslation('documents');
    const document = useGetDocument();
    const restrictions = useEntityTemplateParameters(block, 'block', document);
    const { insertBlock, generateKey, insertBlocks } = useContext(EditAreaContext);
    const [insertBlockIndex] = useQueryParam('ibi', NumberParam);

    const parentBlockTemplateParameters = block.properties?.templateParameters;

    return (
        <div className="card mb-5">
            <div className="card-body p-2">
                <div
                    className={cx(`edit-area-block-type-${block.type}`, {
                        [`blockLayout-type-${block.properties.type ?? 'default'}`]:
                            block.type === Constants.blockTypes.blockLayout,
                    })}
                >
                    <div
                        className="d-flex justify-content-between position-absolute px-2"
                        style={{ left: 0, right: 0, top: -15 }}
                    >
                        <div className="flex-center">
                            <BlockName block={block} />
                        </div>

                        <div className="editor-toolbar">
                            <BlockType block={block} />
                        </div>
                    </div>

                    <DragDropContext onDragEnd={() => {}} isDropDisabled={true}>
                        <Droppable
                            type="BLOCK"
                            key="add-template-block-droppable"
                            droppableId="add-template-block-droppable"
                        >
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    <BlockTypeContent
                                        block={block}
                                        index={index}
                                        readOnly={true}
                                        areaBlocks={areaBlocks}
                                        restrictions={restrictions}
                                    />
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </div>
            <div className="card-footer">
                <InfoButton className="d-flex align-items-center ml-2" onClick={handleInsertBlock}>
                    <ChevronLeft size={16} className="mr-1" />
                    {t('document.navbar.main.btn.useText')}
                </InfoButton>
            </div>
        </div>
    );

    function handleInsertBlock() {
        const documentVariants = variants;

        // Make sure baseVariantId is always included
        if (documentVariants.includes(baseVariantId) === false) {
            documentVariants.push(baseVariantId);
        }

        if (block.type === Constants.blockTypes.blockLayout) {
            const currentBlockData = HelperFunctions.tryParseJSON(block.baseContent);

            if (currentBlockData && currentBlockData.data) {
                const blocksToInsert = [];
                const randomIds = [];

                currentBlockData.data = currentBlockData.data.map((rows) => {
                    return rows.map((columns) => {
                        return columns.map((item) => {
                            if (!item.id) {
                                return item;
                            }

                            const innerBlock = areaBlocks.find((block) => block.id === item.id);

                            if (!innerBlock) {
                                return item;
                            }

                            const newContentBlock = createNewContentBlock(innerBlock, documentVariants, randomIds);
                            blocksToInsert.push(newContentBlock);
                            randomIds.push(newContentBlock.key);

                            return {
                                id: newContentBlock.key,
                                new: true,
                            };
                        });
                    });
                });

                const parentLayoutBlock = {
                    id: null,
                    key: generateKey(randomIds),
                    documentVariants,
                    latestContent: JSON.stringify(currentBlockData),
                    properties: {
                        ...block.properties,
                        templateParameters: parentBlockTemplateParameters,
                        parentId: block.id,
                    },
                    type: block.type,
                    sortOrder: insertBlockIndex,
                };

                blocksToInsert.push(parentLayoutBlock);
                insertBlocks(blocksToInsert);
            }
        } else {
            const blockWithVariants = {
                id: null,
                key: generateKey(),
                documentVariants,
                latestContent: block.baseContent,
                properties: {
                    ...block.properties,
                    templateParameters: parentBlockTemplateParameters,
                    parentId: block.id,
                },
                type: block.type,
            };

            insertBlock(blockWithVariants, insertBlockIndex);
        }

        closeModal();
    }

    function createNewContentBlock(innerBlock, documentVariants, randomIds) {
        return {
            id: null,
            key: generateKey(randomIds),
            documentVariants,
            latestContent: innerBlock.baseContent,
            properties: {
                ...innerBlock.properties,
                templateParameters: parentBlockTemplateParameters,
                parentId: innerBlock.id,
            },
            type: innerBlock.type,
        };
    }
}
